<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">自動貼標規則</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-primary" @click="handleCreate">
          <i class="ri-add-line"></i>
          新增規則
        </button>
      </div>
    </div>

    <div class="page-container">
    <!-- 自動貼標規則說明 -->
    <div class="list-page-intro" :class="{ 'is-expanded': introOpen }">
      <button type="button" class="intro-toggle" @click="introOpen = !introOpen">
        <span class="intro-title">
          <i class="ri-information-line"></i>
          自動貼標規則的作用
        </span>
        <span class="intro-toggle-action">
          <span class="intro-toggle-label">{{ introOpen ? '收起' : '查看說明' }}</span>
          <i :class="introOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
        </span>
      </button>
      <div v-show="introOpen" class="intro-body">
        <p class="intro-text">依會員屬性、消費行為、標籤等條件，自動為符合的會員貼上指定標籤；當會員不再符合條件時，系統也會自動移除該標籤。</p>
        <ul class="intro-list">
          <li><strong>使用範圍</strong>：貼上的標籤可用於 LINE 推播分眾、行銷活動、受眾規則、批量發券／發點等需要依標籤篩選會員的功能。</li>
          <li><strong>執行時機</strong>：可設定「註冊時」「消費後」「每日批次」等觸發方式；也可在列表中手動「執行」立即更新。</li>
          <li><strong>條件組合</strong>：支援多條件 AND／OR 邏輯，並可設定優先順序；列表可「查看會員」預覽目前符合人數。</li>
          <li><strong>提示</strong>：僅「啟用」中的規則會自動執行；標記「內建」的系統規則不可刪除，但可停用或編輯條件。</li>
        </ul>
      </div>
    </div>

    <!-- 搜尋表單 -->
    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchForm.tagName"
          type="text"
          class="form-control"
          placeholder="搜尋標籤名稱..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group">
        <select v-model="searchForm.isActive" class="form-control" @change="handleSearch">
          <option value="">全部啟用狀態</option>
          <option :value="true">啟用</option>
          <option :value="false">停用</option>
        </select>
      </div>
      <button class="btn btn-secondary" @click="handleReset">
        <i class="ri-refresh-line"></i>
        重置
      </button>
    </div>

    <!-- 載入狀態 -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <!-- 規則列表 -->
    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>規則名稱</th>
            <th>規則說明</th>
            <th>標籤</th>
            <th>執行設定</th>
            <th>啟用</th>
            <th>符合會員數</th>
            <th>最後執行</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="rule in rules" :key="rule.id">
            <td>
              {{ rule.ruleName }}
              <span v-if="rule.isBuiltIn" class="built-in-badge" title="系統內建規則">
                <i class="ri-shield-star-line"></i> 內建
              </span>
            </td>
            <td>{{ rule.ruleDescription || '-' }}</td>
            <td>
              <span class="tag-badge" :style="{ backgroundColor: rule.tagColor, borderColor: rule.tagColor }">
                {{ rule.tagName }}
              </span>
            </td>
            <td>
              <div class="execution-settings">
                <span v-if="rule.executeOnRegistration" class="badge badge-success">註冊時</span>
                <span v-if="rule.executeOnPurchase" class="badge badge-warning">消費後</span>
                <span v-if="rule.executeDaily" class="badge badge-info">每日批次</span>
              </div>
            </td>
            <td class="text-center">
              <div class="active-status-cell">
                <span
                  class="badge"
                  :class="rule.isActive ? 'badge-success' : 'badge-secondary'"
                >
                  {{ rule.isActive ? '啟用' : '停用' }}
                </span>
                <label class="switch">
                  <input type="checkbox" v-model="rule.isActive" @change="handleToggleActive(rule)" />
                  <span class="slider"></span>
                </label>
              </div>
            </td>
            <td class="text-center">{{ rule.matchedMemberCount || 0 }}</td>
            <td>
              <div v-if="rule.lastExecutedAt || rule.lastExecutionStatus">
                <div v-if="rule.lastExecutedAt">{{ formatDate(rule.lastExecutedAt) }}</div>
                <span
                  v-if="rule.lastExecutionStatus"
                  class="badge"
                  :class="executionStatusClass(rule.lastExecutionStatus)"
                >
                  {{ executionStatusLabel(rule.lastExecutionStatus) }}
                </span>
              </div>
              <span v-else>尚未執行</span>
            </td>
            <td>
              <div class="action-buttons">
                <button class="btn btn-sm btn-secondary" @click="handlePreviewMembers(rule)" title="查看符合會員">
                  <i class="ri-group-line"></i>
                  查看會員
                </button>
                <button class="btn btn-sm btn-info" @click="handleExecute(rule)" title="執行規則">
                  <i class="ri-play-line"></i>
                  執行
                </button>
                <button class="btn btn-sm btn-primary" @click="handleEdit(rule)" title="編輯">
                  <i class="ri-edit-line"></i>
                  編輯
                </button>
                <button 
                  v-if="!rule.isBuiltIn" 
                  class="btn btn-sm btn-danger" 
                  @click="handleDelete(rule)" 
                  title="刪除"
                >
                  <i class="ri-delete-bin-line"></i>
                  刪除
                </button>
                <button 
                  v-else 
                  class="btn btn-sm btn-secondary" 
                  disabled
                  title="系統內建規則不可刪除"
                >
                  <i class="ri-lock-line"></i>
                  不可刪除
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="rules.length === 0" class="empty-state">
        <p>暫無規則</p>
      </div>
    </div>

    <!-- 會員預覽彈窗 -->
    <div v-if="showPreviewModal" class="modal-overlay" @click.self="closePreviewModal">
      <div class="modal-dialog">
        <div class="modal-header">
          <h3>符合條件的會員列表</h3>
          <button class="btn-close" @click="closePreviewModal">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="previewRule" class="preview-info">
            <p><strong>規則名稱：</strong>{{ previewRule.ruleName }}</p>
            <p><strong>標籤：</strong>{{ previewRule.tagName }}</p>
            <p><strong>符合會員總數：</strong>{{ previewPagination.total }}</p>
          </div>

          <div v-if="previewLoading" class="loading-state">
            <div class="spinner"></div>
            <p>載入中...</p>
          </div>

          <div v-else-if="previewMembers.length === 0" class="empty-state">
            <p>沒有符合條件的會員</p>
          </div>

          <div v-else class="preview-table">
            <table>
              <thead>
                <tr>
                  <th>會員編號</th>
                  <th>姓名</th>
                  <th>手機</th>
                  <th>會員等級</th>
                  <th>消費次數</th>
                  <th>總消費金額</th>
                  <th>註冊時間</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="member in previewMembers" :key="member.id">
                  <td>{{ member.memberCode }}</td>
                  <td>{{ member.name }}</td>
                  <td>{{ member.phone }}</td>
                  <td>{{ member.memberTier }}</td>
                  <td class="text-center">{{ member.purchaseCount || 0 }}</td>
                  <td class="text-right">{{ member.totalSpent || 0 }}</td>
                  <td>{{ formatDate(member.createdAt) }}</td>
                </tr>
              </tbody>
            </table>

            <!-- 分頁 -->
            <ListPagination
              v-if="previewPagination.totalPages > 1"
              :page="previewPagination.page"
              :total-pages="previewPagination.totalPages"
              :total="previewPagination.total"
              @change="handlePreviewPageChange"
            />
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { autoTagRuleService } from '@/api/modules/autoTagRules';
import { showSuccess, showError, showConfirm } from '@/utils/swal.js';

export default {
  name: 'AutoTagRulesList',
  setup() {
    const router = useRouter();
    const loading = ref(false);
    const introOpen = ref(false);
    const rules = ref([]);

    const searchForm = reactive({
      tagName: '',
      isActive: ''
    });

    // 格式化日期
    const formatDate = (date) => {
      if (!date) return '-';
      return new Date(date).toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    };

    // 載入規則列表
    const loadRules = async () => {
      loading.value = true;
      try {
        const params = {};
        if (searchForm.tagName) params.tagName = searchForm.tagName;
        if (searchForm.isActive !== '') params.isActive = searchForm.isActive;

        const response = await autoTagRuleService.getAutoTagRules(params);
        rules.value = response.data || [];
      } catch (error) {
        console.error('載入規則列表失敗:', error);
        await showError('載入失敗', '載入規則列表失敗');
      } finally {
        loading.value = false;
      }
    };

    // 搜尋
    const handleSearch = () => {
      loadRules();
    };

    // 重置
    const handleReset = () => {
      searchForm.tagName = '';
      searchForm.isActive = '';
      loadRules();
    };

    // 新增規則
    const handleCreate = () => {
      router.push('/client/auto-tag-rules/create');
    };

    // 編輯規則
    const handleEdit = (row) => {
      router.push(`/client/auto-tag-rules/edit/${row.id}`);
    };

    // 預覽符合條件的會員
    const showPreviewModal = ref(false);
    const previewRule = ref(null);
    const previewMembers = ref([]);
    const previewLoading = ref(false);
    const previewPagination = ref({
      page: 1,
      pageSize: 20,
      total: 0,
      totalPages: 0
    });

    const handlePreviewMembers = async (rule) => {
      previewRule.value = rule;
      showPreviewModal.value = true;
      await loadPreviewMembers();
    };

    const loadPreviewMembers = async () => {
      if (!previewRule.value) return;

      previewLoading.value = true;
      try {
        const response = await autoTagRuleService.previewMatchingMembers(
          previewRule.value.id,
          {
            page: previewPagination.value.page,
            pageSize: previewPagination.value.pageSize
          }
        );
        previewMembers.value = response.data.members || [];
        previewPagination.value = response.data.pagination;
      } catch (error) {
        console.error('載入會員列表失敗:', error);
        await showError('載入失敗', '載入會員列表失敗');
      } finally {
        previewLoading.value = false;
      }
    };

    const closePreviewModal = () => {
      showPreviewModal.value = false;
      previewRule.value = null;
      previewMembers.value = [];
      previewPagination.value = {
        page: 1,
        pageSize: 20,
        total: 0,
        totalPages: 0
      };
    };

    const handlePreviewPageChange = (page) => {
      previewPagination.value.page = page;
      loadPreviewMembers();
    };

    // 執行規則
    const handleExecute = async (row) => {
      try {
        const confirmed = await showConfirm(
          '確認執行',
          `確定要執行規則「${row.ruleName}」嗎？`,
          '確定',
          '取消',
          'warning'
        );

        if (!confirmed) return;

        loading.value = true;
        const response = await autoTagRuleService.executeAutoTagRule(row.id);
        if (response.data?.queued) {
          await showSuccess('已排入執行', '這條規則已排入執行');
        } else if (response.data?.message === '這條規則正在執行') {
          await showError('無法執行', '這條規則正在執行');
        } else {
          await showSuccess('執行成功', `符合條件的會員數：${response.data.matchedCount}`);
        }
        await loadRules();
      } catch (error) {
        console.error('執行規則失敗:', error);
        await showError('執行失敗', error.message || '執行規則失敗');
      } finally {
        loading.value = false;
      }
    };

    // 啟用/停用規則
    const handleToggleActive = async (row) => {
      try {
        await autoTagRuleService.toggleAutoTagRule(row.id, row.isActive);
        await showSuccess('更新成功', row.isActive ? '規則已啟用' : '規則已停用', 1500);
      } catch (error) {
        console.error('更新規則狀態失敗:', error);
        await showError('更新失敗', '更新規則狀態失敗');
        row.isActive = !row.isActive; // 恢復原狀態
      }
    };

    // 刪除規則
    const handleDelete = async (row) => {
      try {
        const confirmed = await showConfirm(
          '確認刪除',
          `確定要刪除規則「${row.ruleName}」嗎？會一併移除標籤「${row.tagName || ''}」，以及會員身上的這個標籤。`,
          '刪除',
          '取消',
          'warning'
        );

        if (!confirmed) return;

        loading.value = true;
        await autoTagRuleService.deleteAutoTagRule(row.id);
        await showSuccess('刪除成功', '');
        await loadRules();
      } catch (error) {
        console.error('刪除規則失敗:', error);
        await showError('刪除失敗', '刪除規則失敗');
      } finally {
        loading.value = false;
      }
    };

    const executionStatusLabel = (status) => ({
      queued: '排隊中',
      running: '執行中',
      success: '成功',
      failed: '失敗'
    }[status] || status);

    const executionStatusClass = (status) => ({
      queued: 'badge-warning',
      running: 'badge-info',
      success: 'badge-success',
      failed: 'badge-danger'
    }[status] || 'badge-secondary');

    onMounted(() => {
      loadRules();
    });

    return {
      loading,
      introOpen,
      rules,
      searchForm,
      formatDate,
      loadRules,
      handleSearch,
      handleReset,
      handleCreate,
      handleEdit,
      handlePreviewMembers,
      showPreviewModal,
      previewRule,
      previewMembers,
      previewLoading,
      previewPagination,
      closePreviewModal,
      handlePreviewPageChange,
      handleExecute,
      executionStatusLabel,
      executionStatusClass,
      handleToggleActive,
      handleDelete
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

/* 彈窗樣式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-dialog {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.btn-close:hover {
  background: #f5f5f5;
  color: #333;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.preview-info {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 6px;
  margin-bottom: 20px;
}

.preview-info p {
  margin: 8px 0;
  font-size: 14px;
  color: #555;
}

.preview-info strong {
  color: #333;
  margin-right: 8px;
}

.preview-table {
  margin-top: 16px;
}

.preview-table table {
  width: 100%;
  border-collapse: collapse;
}

.preview-table thead th {
  background: #f8f9fa;
  padding: 12px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  color: #555;
  border-bottom: 2px solid #dee2e6;
}

.preview-table tbody td {
  padding: 12px;
  border-bottom: 1px solid #eee;
  font-size: 13px;
  color: #333;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.page-info {
  font-size: 14px;
  color: #666;
}

.text-center {
  text-align: center;
}

.text-right {
  text-align: right;
}

/* 內建規則標籤 */
.built-in-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  padding: 2px 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.built-in-badge i {
  font-size: 12px;
}

.filter-group {
  min-width: 150px;
  display: block;
  margin-bottom: 0;
  flex-shrink: 0;
}

.execution-settings {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.tag-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  color: #fff;
  font-size: 12px;
  border: 1px solid;
}

.active-status-cell {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.active-status-cell .badge {
  min-width: 44px;
}

/* Switch 樣式 */
.switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #667eea;
}

input:checked + .slider:before {
  transform: translateX(20px);
}
</style>
